import { useEffect, useState, useRef, useCallback, lazy, Suspense } from 'react';
import logoImg from './logo.png';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';

import { K } from './constants';
import { usePersist } from './hooks/usePersist';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { useQuizHistory } from './hooks/useQuizHistory';
import { useUserRole } from './hooks/useUserRole';
import { usePreconnects } from './hooks/usePreconnects';
import { useCustomQuestions } from './hooks/useCustomQuestions';
import { useOnline } from './hooks/useOnline';
import { useIncident } from './hooks/useIncident';
import { useChecklistTemplates } from './hooks/useChecklistTemplates';
import { useApparatus } from './hooks/useApparatus';
import { useToolStatus, DEFAULT_OFF } from './hooks/useToolStatus';
import { useVersionCheck } from './hooks/useVersionCheck';
import { useCalcDefaults } from './hooks/useCalcDefaults';
import { IncidentBar, AuthGate } from './components/ui';
import { Icon } from './components/Icon';
import { AuthModal } from './screens/AuthModal';
import { CalcOverridesModal } from './screens/CalcOverridesModal';
import { ToastProvider } from './components/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';

// Eagerly loaded — home + ops mode (default)
import { HomePage }     from './screens/HomePage';
import { OpsHome }      from './screens/OpsHome';
import { HoseLayout }   from './screens/HoseLayout';
import { Preconnects }  from './screens/Preconnects';
import { WaterSupply }  from './screens/WaterSupply';
import { RelayPump }    from './screens/RelayPump';
import { CustomCalc }   from './screens/CustomCalc';
import { Apparatus }    from './screens/Apparatus';
import { Resources }    from './screens/Resources';
import { Chat }         from './screens/Chat';
import { FeedbackModal } from './screens/FeedbackModal';
import { RadioFAB } from './screens/Radio';
import { useAnnouncements } from './hooks/useAnnouncements';
import { useChatUnread } from './hooks/useChatUnread';
import { AnnouncementFAB, AnnouncementPopup } from './screens/Announcements';
import { useUserProfile } from './hooks/useUserProfile';
import { ProfileAvatar, ProfileModal } from './screens/ProfileModal';

// Lazy loaded — training mode
const TrainingHome = lazy(() => import('./screens/TrainingHome').then(m => ({ default: m.TrainingHome })));
const Quiz         = lazy(() => import('./screens/Quiz').then(m => ({ default: m.Quiz })));
const Flashcards   = lazy(() => import('./screens/Flashcards').then(m => ({ default: m.Flashcards })));
const Leaderboard  = lazy(() => import('./screens/Leaderboard').then(m => ({ default: m.Leaderboard })));
const Evolutions   = lazy(() => import('./screens/Evolutions').then(m => ({ default: m.Evolutions })));
const TrainingHub  = lazy(() => import('./screens/TrainingHub').then(m => ({ default: m.TrainingHub })));
// 3D pump simulator — its own chunk so three.js never loads with the calculators.
const SimScreen   = lazy(() => import('./sim/SimScreen').then(m => ({ default: m.SimScreen })));
const Podcast     = lazy(() => import('./screens/Podcast').then(m => ({ default: m.Podcast })));
const FLRef        = lazy(() => import('./screens/FLRef').then(m => ({ default: m.FLRef })));
const GPMCalc      = lazy(() => import('./screens/GPMCalc').then(m => ({ default: m.GPMCalc })));
const MultiLine    = lazy(() => import('./screens/MultiLine').then(m => ({ default: m.MultiLine })));
const Drafting     = lazy(() => import('./screens/Drafting').then(m => ({ default: m.Drafting })));

// Lazy loaded — command mode
const CommandHome      = lazy(() => import('./screens/CommandHome').then(m => ({ default: m.CommandHome })));
const IncidentCommand  = lazy(() => import('./screens/IncidentCommand').then(m => ({ default: m.IncidentCommand })));
const IncidentHistory  = lazy(() => import('./screens/IncidentHistory').then(m => ({ default: m.IncidentHistory })));

// Lazy loaded — EMS mode
const EmsHome     = lazy(() => import('./screens/EmsHome').then(m => ({ default: m.EmsHome })));
const CprTimer    = lazy(() => import('./screens/CprTimer').then(m => ({ default: m.CprTimer })));
const DrugCalc    = lazy(() => import('./screens/DrugCalc').then(m => ({ default: m.DrugCalc })));
const Assessment  = lazy(() => import('./screens/Assessment').then(m => ({ default: m.Assessment })));
const BurnCalc    = lazy(() => import('./screens/BurnCalc').then(m => ({ default: m.BurnCalc })));
const Pediatric   = lazy(() => import('./screens/Pediatric').then(m => ({ default: m.Pediatric })));
const EcgPractice = lazy(() => import('./screens/EcgPractice').then(m => ({ default: m.EcgPractice })));

// Lazy loaded — admin
const AdminPanel     = lazy(() => import('./screens/AdminPanel').then(m => ({ default: m.AdminPanel })));
const Radio          = lazy(() => import('./screens/Radio').then(m => ({ default: m.Radio })));
const Announcements  = lazy(() => import('./screens/Announcements').then(m => ({ default: m.Announcements })));

/* Tabs where the Math button is relevant (showMath prop) */
const MATH_TABS = new Set(['layout', 'supply', 'relay', 'custom', 'pc', 'flref', 'gpm', 'multiline', 'draft']);

const NAV_TABS = [
  { id: 'ops',      label: 'Pump',     icon: 'ops' },
  { id: 'command',  label: 'Command',  icon: 'command' },
  { id: 'home',     label: 'Home',     icon: 'home' },
  { id: 'training', label: 'Training', icon: 'training' },
  { id: 'ems',      label: 'EMS',      icon: 'ems' },
];

function LazyFallback() {
  return <div style={{ textAlign: 'center', padding: '60px 20px', color: K.t3, fontSize: 12 }}>Loading...</div>;
}

function AppShell() {
  const [tab, setTab] = useState('home');
  const [mode, setMode] = useState(null);
  const [showMath, setShowMath] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [user, setUser] = useState(null);
  const [guestScore, setGuestScore] = usePersist('hydro_quiz_score', { correct: 0, total: 0 });
  const { score: quizScore, logAnswer, resetScore } = useQuizHistory(user, guestScore, setGuestScore);
  const role        = useUserRole(user);
  const isAdmin     = role === 'admin';
  const isOfficer   = role === 'officer';
  const canEdit     = isAdmin || isOfficer;
  const preconnects = usePreconnects();
  const { questions: customQuestions } = useCustomQuestions();
  const { online, syncing } = useOnline();
  const incident = useIncident(user);
  const checklistTemplates = useChecklistTemplates();
  const apparatus = useApparatus();
  const { isEnabled } = useToolStatus();
  // Off-by-default tools (DEFAULT_OFF) stay hidden from members until switched on; admins can still open them.
  const canOpen = useCallback((id) => isEnabled(id) || (isAdmin && DEFAULT_OFF.has(id)), [isEnabled, isAdmin]);
  const calcDefaultsApi = useCalcDefaults();
  const { defaults: calcDefaults, hasSessionOverrides } = calcDefaultsApi;
  const [showOverrides, setShowOverrides] = useState(false);
  const { updateAvailable, reload } = useVersionCheck();
  const announcementsData = useAnnouncements(user);
  const chatUnread = useChatUnread(user);
  const { theme, toggleTheme } = useTheme();
  const { profile } = useUserProfile(user);
  const [showProfile, setShowProfile] = useState(false);
  const [pageKey, setPageKey] = useState(0);
  const mainRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return unsub;
  }, []);

  // When the user opens the Chat tab, clear their unread count.
  useEffect(() => {
    if (tab === 'chat' && user) chatUnread.markRead();
  }, [tab, user, chatUnread]);

  // Auto-seed data to Firestore when an admin loads the app
  useEffect(() => {
    if (!isAdmin) return;
    import('./utils/seedData').then(({ autoSeed }) => {
      autoSeed().then((res) => {
        if (res.seeded.length > 0) {
          console.log('[auto-seed] Seeded:', res.seeded.join(', '));
        }
        if (res.errors.length > 0) {
          console.warn('[auto-seed] Errors:', res.errors);
        }
      });
    });
  }, [isAdmin]);

  // Detect standalone PWA mode (home screen app) for safe-area CSS
  useEffect(() => {
    const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) document.body.classList.add('pwa-standalone');
  }, []);

  const accentCol = mode === 'ops' ? K.red : mode === 'command' ? K.info : mode === 'ems' ? K.ems : mode === 'training' ? K.gold : K.red;

  const goTab = (id) => {
    setTab(id);
    setPageKey((k) => k + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (mode !== null) {
      setTab('home');
      setPageKey((k) => k + 1);
      window.scrollTo({ top: 0 });
    }
  }, [mode]);

  return (
    <div style={{ minHeight: '100vh', background: K.bg, color: K.t0, fontFamily: K.fSans }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar-thumb { background: ${K.bg3}; }
      `}</style>

      <header style={{ background: K.bg1, borderBottom: `3px solid ${accentCol}`, padding: 'env(safe-area-inset-top, 0px) 10px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 54, position: 'sticky', top: 0, zIndex: 300, boxShadow: `0 4px 24px ${K.bg}` }}>
        {/* Left: Home + Logo */}
        <button onClick={() => { setMode(null); goTab('home'); }} aria-label="Go to home screen" style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
          <img src={logoImg} alt="CMFE" style={{ width: 32, height: 28, objectFit: 'contain' }} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontFamily: K.fSans, fontSize: 18, fontWeight: 700, letterSpacing: 0.2, lineHeight: 1, color: K.t0 }}>Hydro<span style={{ color: accentCol }}>Calc</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
              <span style={{ fontSize: 8, color: K.t2, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>CMFE</span>
              <span style={{ fontSize: 6, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: K.warn, background: `${K.warn}18`, border: `1px solid ${K.warn}44`, borderRadius: 2, padding: '1px 4px', lineHeight: 1.3 }}>Beta</span>
            </div>
          </div>
        </button>

        {/* Right: theme | admin | profile */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="hc-btn" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} style={{ background: K.bg3, border: `1px solid ${K.bdr}`, padding: '6px', borderRadius: 8, cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {theme === 'dark' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={K.warn} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={K.info} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
          {canEdit && (
            <button className="hc-btn" onClick={() => goTab('admin')} aria-label="Admin panel" style={{ background: tab === 'admin' ? `${K.gold}22` : K.bg3, border: `1px solid ${tab === 'admin' ? K.gold : K.bdr}`, padding: '6px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="settings" size={16} color={tab === 'admin' ? K.gold : K.t2} strokeWidth={1.75} />
            </button>
          )}
          {user ? (
            <ProfileAvatar photoUrl={profile?.photoUrl} name={profile?.displayName || user.email} onClick={() => setShowProfile(true)} />
          ) : (
            <button className="hc-btn" onClick={() => setShowAuth(true)} aria-label="Log in" style={{ background: K.red, border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: K.fSans }}>Log in</button>
          )}
        </div>
      </header>

      {!online && (
        <div style={{ background: K.warn, color: '#f5f5f5', textAlign: 'center', padding: '6px 14px', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
          ⚡ Offline — changes will sync when reconnected
        </div>
      )}
      {online && syncing && (
        <div style={{ background: K.info, color: '#f5f5f5', textAlign: 'center', padding: '6px 14px', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
          ⟳ Reconnected — syncing pending changes…
        </div>
      )}

      {updateAvailable && (
        <button onClick={reload} style={{ display: 'block', width: '100%', background: K.info, color: '#f5f5f5', textAlign: 'center', padding: '8px 14px', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', border: 'none', cursor: 'pointer' }}>
          New version available — tap to update
        </button>
      )}

      {incident.incident?.status === 'active' && tab !== 'incident' && (
        <IncidentBar incident={incident.incident} elapsedFmt={incident.elapsedFmt} onView={() => goTab('incident')} />
      )}

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {showProfile && user && <ProfileModal user={user} onClose={() => setShowProfile(false)} />}
      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} user={user} />}
      {showOverrides && (
        <CalcOverridesModal
          adminDefaults={calcDefaultsApi.adminDefaults}
          sessionOverrides={calcDefaultsApi.sessionOverrides}
          setSessionOverride={calcDefaultsApi.setSessionOverride}
          resetSessionField={calcDefaultsApi.resetSessionField}
          resetAllSession={calcDefaultsApi.resetAllSession}
          hasSessionOverrides={hasSessionOverrides}
          onClose={() => setShowOverrides(false)}
        />
      )}

      <main ref={mainRef} style={{ padding: '20px 14px calc(78px + env(safe-area-inset-bottom, 0px))', maxWidth: tab === 'sim' ? 1600 : 860, margin: '0 auto' }}>
        {/* Contextual calc toolbar — only visible on calculator screens */}
        {MATH_TABS.has(tab) && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${K.bdr}` }}>
            <button
              onClick={() => setShowMath((m) => !m)}
              style={{
                background: showMath ? `${accentCol}18` : K.bg2,
                border: `1px solid ${showMath ? accentCol : K.bdr}`,
                color: showMath ? accentCol : K.t2,
                borderRadius: 8, padding: '6px 13px',
                cursor: 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: K.fSans, lineHeight: 1.3,
              }}
            >
              {showMath ? 'Hide math' : 'Show math'}
            </button>
            <button
              onClick={() => setShowOverrides(true)}
              title={hasSessionOverrides ? 'Defaults adjusted' : 'Adjust defaults'}
              style={{
                position: 'relative',
                background: hasSessionOverrides ? `${K.gold}18` : K.bg2,
                border: `1px solid ${hasSessionOverrides ? K.gold : K.bdr}`,
                color: hasSessionOverrides ? K.gold : K.t2,
                borderRadius: 8, padding: '6px 13px',
                cursor: 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: K.fSans, lineHeight: 1.3,
              }}
            >
              Adjust defaults
              {hasSessionOverrides && (
                <span style={{ position: 'absolute', top: -3, right: -3, width: 7, height: 7, borderRadius: '50%', background: K.gold, border: `1px solid ${K.bg1}` }} />
              )}
            </button>
          </div>
        )}
        <div key={pageKey} className="hc-page-enter">
          {/* Landing page */}
          {tab === 'home' && mode === null && <HomePage
            onSelectMode={(m) => { setMode(m); goTab('home'); }}
            onQuickTool={(t, m) => { if (m) setMode(m); goTab(t); }}
            incident={incident}
            user={user}
            quizScore={quizScore}
            isEnabled={isEnabled}
          />}

          {/* Public screens — calculators & tools */}
          {tab === 'home' && mode === 'ops'      && <OpsHome goTab={goTab} preconnects={preconnects} isAdmin={canEdit} isEnabled={isEnabled} />}
          {tab === 'layout'    && isEnabled('layout')    && <HoseLayout showMath={showMath} calcDefaults={calcDefaults} />}
          {tab === 'supply'    && isEnabled('supply')    && <WaterSupply showMath={showMath} calcDefaults={calcDefaults} />}
          {tab === 'relay'     && isEnabled('relay')     && <RelayPump showMath={showMath} calcDefaults={calcDefaults} />}
          {tab === 'custom'    && isEnabled('custom')    && <CustomCalc showMath={showMath} calcDefaults={calcDefaults} />}

          {/* Auth-gated screens — department proprietary data */}
          {tab === 'pc'        && isEnabled('pc')        && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} /> : <Preconnects showMath={showMath} preconnects={preconnects} isAdmin={canEdit} calcDefaults={calcDefaults} />)}
          {tab === 'apparatus' && isEnabled('apparatus') && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} /> : <Apparatus isAdmin={canEdit} />)}
          {tab === 'resources' && isEnabled('resources') && <Resources user={user} isAdmin={canEdit} onShowAuth={() => setShowAuth(true)} goTab={goTab} showPodcastLink={isEnabled('podcast')} initialMode={mode === 'ems' ? 'ems' : mode === 'command' ? 'command' : mode === 'training' ? 'general' : 'fire'} />}
          {tab === 'chat'      && isEnabled('chat')      && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} /> : <Chat user={user} isAdmin={isAdmin} />)}

          {/* Lazy-loaded screens */}
          <ErrorBoundary>
          <Suspense fallback={<LazyFallback />}>
            {tab === 'home' && mode === 'command'  && <CommandHome goTab={goTab} incident={incident} user={user} onShowAuth={() => setShowAuth(true)} isEnabled={isEnabled} />}
            {tab === 'home' && mode === 'training' && <TrainingHome goTab={goTab} quizScore={quizScore} user={user} isAdmin={canEdit} isEnabled={canOpen} />}
            {tab === 'home' && mode === 'ems'      && <EmsHome goTab={goTab} isEnabled={isEnabled} />}

            {/* Public lazy screens — calculators & reference */}
            {tab === 'flref'     && isEnabled('flref')     && <FLRef showMath={showMath} calcDefaults={calcDefaults} />}
            {tab === 'gpm'       && isEnabled('gpm')       && <GPMCalc showMath={showMath} calcDefaults={calcDefaults} />}
            {tab === 'multiline' && isEnabled('multiline') && <MultiLine showMath={showMath} calcDefaults={calcDefaults} />}
            {tab === 'draft'     && isEnabled('draft')     && <Drafting showMath={showMath} calcDefaults={calcDefaults} />}
            {tab === 'cprtimer'  && isEnabled('cprtimer')  && <CprTimer />}
            {tab === 'burncalc'  && isEnabled('burncalc')  && <BurnCalc />}
            {tab === 'pediatric' && isEnabled('pediatric') && <Pediatric />}

            {/* Auth-gated lazy screens — proprietary content */}
            {tab === 'incident'   && isEnabled('incident')   && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} /> : <IncidentCommand inc={incident} user={user} onShowAuth={() => setShowAuth(true)} templates={checklistTemplates} apparatus={apparatus.units} />)}
            {tab === 'quiz'       && isEnabled('quiz')       && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} message="Quiz content includes department-specific questions. Log in to access." /> : <Quiz score={quizScore} logAnswer={logAnswer} resetScore={resetScore} customQuestions={customQuestions} />)}
            {tab === 'flashcards' && isEnabled('flashcards') && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} message="Flashcards include department-specific content. Log in to access." /> : <Flashcards />)}
            {tab === 'training-hub' && isEnabled('training-hub') && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} /> : <TrainingHub user={user} isAdmin={canEdit} />)}
            {tab === 'podcast'    && isEnabled('podcast')    && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} /> : <Podcast user={user} isAdmin={canEdit} />)}
            {tab === 'evolutions' && isEnabled('evolutions') && <Evolutions />}
            {tab === 'sim'        && canOpen('sim')          && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} /> : <SimScreen presets={preconnects} />)}
            {tab === 'leaderboard' && isEnabled('leaderboard') && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} /> : <Leaderboard currentUser={user} />)}
            {tab === 'history'    && isEnabled('history')    && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} /> : <IncidentHistory user={user} isAdmin={canEdit} />)}
            {tab === 'drugcalc'   && isEnabled('drugcalc')   && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} message="Drug formulary data is restricted to members. Log in to access." /> : <DrugCalc />)}
            {tab === 'assessment' && isEnabled('assessment') && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} /> : <Assessment />)}
            {tab === 'ecg'        && isEnabled('ecg')        && (!user ? <AuthGate onShowAuth={() => setShowAuth(true)} /> : <EcgPractice />)}
            {tab === 'radio'      && isEnabled('radio')      && <Radio isAdmin={canEdit} />}
            {tab === 'notices'    && isEnabled('notices')    && <Announcements {...announcementsData} isAdmin={canEdit} />}
            {tab === 'admin'      && canEdit && <AdminPanel currentUser={user} role={role} />}

            {/* Fallback for disabled tools */}
            {tab !== 'home' && tab !== 'admin' && !canOpen(tab) && (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🚧</div>
                <div style={{ fontSize: 16, color: K.t1, fontWeight: 700, marginBottom: 6 }}>Tool Offline</div>
                <div style={{ fontSize: 13, color: K.t3 }}>This tool has been temporarily disabled by an administrator.</div>
              </div>
            )}
          </Suspense>
          </ErrorBoundary>
        </div>
      </main>

      <footer style={{ textAlign: 'center', padding: `24px 14px calc(20px + 60px + env(safe-area-inset-bottom, 0px))`, borderTop: `1px solid ${K.bdr}` }}>
        <div style={{ fontFamily: K.fSans, fontSize: 15, fontWeight: 700, color: K.red }}>Central Mason Fire & EMS</div>
        <div style={{ fontFamily: K.fSans, fontSize: 12, color: K.t2, marginTop: 4, fontWeight: 500 }}>HydroCalc v3 <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: K.warn, background: `${K.warn}18`, border: `1px solid ${K.warn}44`, borderRadius: 3, padding: '1px 6px', verticalAlign: 'middle' }}>Beta</span> · Created by <span style={{ color: K.t1 }}>Colby Black</span></div>
        <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t3, marginTop: 4, fontWeight: 500 }}>CubbyCo</div>
        <button className="hc-btn" onClick={() => setShowFeedback(true)} style={{ marginTop: 12, background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 8, padding: '8px 18px', cursor: 'pointer', color: K.t2, fontSize: 12, fontFamily: K.fSans, fontWeight: 500 }}>
          Feedback & Support
        </button>
      </footer>

      {/* Announcement popup for unread notices */}
      {user && announcementsData.unreadCount > 0 && tab !== 'notices' && (
        <AnnouncementPopup
          announcements={announcementsData.announcements}
          readIds={announcementsData.readIds}
          markAllRead={announcementsData.markAllRead}
          onView={() => goTab('notices')}
        />
      )}

      {/* Announcements FAB */}
      <AnnouncementFAB unreadCount={announcementsData.unreadCount} onClick={() => goTab('notices')} />

      {/* Radio FAB */}
      <RadioFAB onClick={() => goTab('radio')} />

      {/* Chat FAB */}
      {user && (
        <button onClick={() => goTab('chat')}
          aria-label={chatUnread.unreadCount > 0 ? `Open chat (${chatUnread.unreadCount} unread)` : 'Open chat'}
          style={{
          position: 'fixed', bottom: 162, right: 12, zIndex: 400,
          width: 44, height: 44, borderRadius: '50%',
          background: K.bg1, border: `1.5px solid ${chatUnread.unreadCount > 0 ? K.red : K.teal}`,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={chatUnread.unreadCount > 0 ? K.red : K.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          {chatUnread.unreadCount > 0 && (
            <span style={{
              position: 'absolute', top: -4, right: -4,
              minWidth: 18, height: 18, padding: '0 5px',
              borderRadius: 9, background: K.red, color: '#fff',
              fontSize: 10, fontWeight: 800, fontFamily: "'DM Mono', monospace",
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `2px solid ${K.bg1}`, lineHeight: 1,
            }}>
              {chatUnread.capped ? '50+' : chatUnread.unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Bottom Nav Bar */}
      <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 300, background: K.bg1, borderTop: `1px solid ${K.bdr}`, display: 'flex', minHeight: 56, paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: `0 -4px 24px ${K.bg}` }}>
        {NAV_TABS.map((t) => {
          const active = t.id === 'home' ? mode === null : mode === t.id;
          const col = t.id === 'command' ? K.info : t.id === 'training' ? K.gold : t.id === 'ems' ? K.ems : K.red;
          return (
            <button key={t.id} className="hc-btn" onClick={() => { setMode(t.id === 'home' ? null : t.id); setTab('home'); setPageKey((k) => k + 1); window.scrollTo({ top: 0 }); }} aria-label={t.id === 'home' ? 'Go to home screen' : `Switch to ${t.label}`}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, background: 'transparent', border: 'none', cursor: 'pointer', padding: '7px 0 5px', transition: 'all .15s' }}>
              <div style={{ width: 38, height: 24, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: active ? `${col}26` : 'transparent', transition: 'background .15s' }}>
                <Icon name={t.icon} size={19} color={active ? col : K.t2} strokeWidth={active ? 2 : 1.75} />
              </div>
              <span style={{ fontFamily: K.fSans, fontSize: 10.5, fontWeight: active ? 600 : 500, color: active ? col : K.t2, letterSpacing: 0.2 }}>{t.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <ThemeProvider>
          <AppShell />
        </ThemeProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
